import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter, type Event } from '@patsnap/tahiti-core/common/event';
import { OriginalInputModel } from '../model/original-input.model';
import type { OriginalInputState, SupportedTargetLanguage } from '../type/ai-translation.type';
import { SupportedLanguage } from '../type/ai-translation.enum';

const LANGUAGE_CHECK_LENGTH = 300;
const LANGUAGE_CHECK_PERCENT = 0.5;
const cnRange = [0x4e00, 0x9fa5];
const enRange = [0x21, 0x7e];

export const OriginalInputService = Symbol('OriginalInputService');

export interface OriginalInputService {
    getModelData: () => OriginalInputState;
    readonly onStatesChange: Event<OriginalInputState>;

    onSubmit: Event<OriginalInputState>;
    updateInput: (input: string) => void;
    updateTargetLang: (targetLang: SupportedTargetLanguage) => void;
    reset: () => void;
    initialize: (params: Partial<OriginalInputState>) => void;
}

@injectable()
export class OriginalInputServiceImpl implements OriginalInputService {
    private _onStatesChangeEmitter = new Emitter<OriginalInputState>();
    readonly onStatesChange = this._onStatesChangeEmitter.event;
    private _onSubmitEmitter = new Emitter<OriginalInputState>();
    public readonly onSubmit = this._onSubmitEmitter.event;

    constructor(
        @inject(OriginalInputModel)
        private _model: OriginalInputModel,
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
    }

    private _emitModelDataChange() {
        this._onStatesChangeEmitter.fire(this.getModelData());
    }

    private _setModelData(value: Partial<OriginalInputState>) {
        this._model.update(modelData => {
            return { ...modelData, ...value };
        });
    }

    public initialize(params: Partial<OriginalInputState>) {
        this._setModelData(params);
    }

    public getModelData() {
        return this._model.modelData;
    }

    // eslint-disable-next-line complexity
    public updateInput(input: string) {
        if (input.length === 0) {
            this._setModelData({ input, isSupportLang: true, targetLang: SupportedLanguage.EN });
            return;
        }

        // 校验语言
        const inputStr = input.slice(0, LANGUAGE_CHECK_LENGTH);
        let cnCount = 0;
        let enCount = 0;
        let otherCount = 0;

        for (let i = 0; i < inputStr.length; i++) {
            const charCode = inputStr.charCodeAt(i);

            if (charCode >= cnRange[0] && charCode <= cnRange[1]) {
                cnCount++;
            } else if (
                charCode >= enRange[0] && charCode <= enRange[1]
            ) {
                enCount++;
            } else {
                otherCount++;
            }
        }

        const total = cnCount + enCount + otherCount;

        if (cnCount / total > LANGUAGE_CHECK_PERCENT) {
            this._setModelData({ input, isSupportLang: true, targetLang: SupportedLanguage.EN, sourceLang: SupportedLanguage.CN });
        } else if (enCount / total > LANGUAGE_CHECK_PERCENT) {
            this._setModelData({ input, isSupportLang: true, targetLang: SupportedLanguage.CN, sourceLang: SupportedLanguage.EN });
        } else {
            this._setModelData({ input, isSupportLang: false });
        }
    }

    public updateTargetLang(targetLang: SupportedTargetLanguage | null) {
        this._setModelData({ targetLang });
    }

    public reset() {
        this._setModelData({
            input: '',
            sourceLang: SupportedLanguage.CN,
            targetLang: SupportedLanguage.EN,
            isSupportLang: true,
        });
    }
}
